import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, RouterLink } from '@angular/router';
import { CartDialog } from '@app/components/cart/cart-dialog/cart-dialog';
import { LocationChangeDialog } from '@app/components/header/location-change-dialog/location-change-dialog';
import { LoginSignupDialog } from '@app/components/header/login-signup-dialog/login-signup-dialog';
import { Sidebar, SidebarNavItem, SidebarSocialIcon } from '@app/components/header/sidebar/sidebar';
import {
  PROFILE_SIDEBAR_ITEMS_MOBILE_VIEW,
  SIDE_MENU_ITEMS,
} from '@app/constants/navigation.constant';
import { RouteEnum } from '@app/constants/route.enum';
import { StringEnum } from '@app/constants/string.enum';
import { UtilityEnum } from '@app/constants/utility.enum';
import { AppDialogCoordinator } from '@app/services/app-dialog-coordinator/app-dialog-coordinator.service';
import { Cart } from '@app/services/cart/cart';
import { GuestCart } from '@app/services/cart/guest-cart';
import { GtmService } from '@app/services/gtm/gtm.service';
import { MyProfile } from '@app/services/my-profile/my-profile';
import { Utility } from '@app/services/utility/utility';
import { AppStore } from '@app/stores/app.store';

@Component({
  selector: 'app-navbar',
  imports: [MatToolbarModule, MatButtonModule, MatIconModule, RouterLink, CommonModule, Sidebar],
  templateUrl: './navbar.html',
  styleUrl: './navbar.scss',
})
export class Navbar implements OnInit {
  protected readonly stringEnum = StringEnum;
  protected readonly utilityEnum = UtilityEnum;
  protected readonly routeEnum = RouteEnum;
  protected readonly appStore = inject(AppStore);
  protected selectedCity = computed(() => this.appStore.selectedCity());
  protected selectedPincode = computed(() => this.appStore.selectedPincode());
  protected isLoggedIn = computed(() => this.appStore.isLoggedIn());
  protected cartItemCount = computed(() => this.appStore.cartItemCount());
  protected userName = computed(() => this.appStore.userName());
  protected contactNo = computed(() => this.appStore.contactNo());
  protected walletBalance = computed(() => this.appStore.userWalletBalanceProfile());
  protected featureToggles = computed(() => this.appStore.featureToggles());

  protected sidebarNavItems: SidebarNavItem[] = this.featureToggles().farmVisitEnabled
    ? SIDE_MENU_ITEMS
    : SIDE_MENU_ITEMS.filter((item) => item.route !== this.routeEnum.OUR_FARM);

  protected sidebarSocialIcons: SidebarSocialIcon[] = [
    { icon: 'fb_icon_custom', alt: 'Facebook', url: UtilityEnum.OV_FB_URL },
    { icon: 'insta_icon_custom', alt: 'Instagram', url: UtilityEnum.OV_INSTA_URL },
    { icon: 'youtube_icon_custom', alt: 'YouTube', url: UtilityEnum.OV_YT_URL },
  ];

  protected sidebarHelpLegalItems: SidebarNavItem[] = [
    {
      label: 'Terms & Conditions',
      route: this.routeEnum.TERMS_AND_CONDITIONS,
    },
    {
      label: 'Privacy Policy',
      route: this.routeEnum.PRIVACY_POLICY,
    },
  ];

  // Profile sidebar configuration - same as my-profile sidebar with icons
  protected profileSidebarNavItems: SidebarNavItem[] = this.featureToggles().testReportEnabled
    ? PROFILE_SIDEBAR_ITEMS_MOBILE_VIEW
    : PROFILE_SIDEBAR_ITEMS_MOBILE_VIEW.filter(
        (item) => item.key !== UtilityEnum.TEST_REPORTS_SECTION,
      );

  private previousLoginState = this.isLoggedIn();
  protected profileSidebarVisible = signal<boolean>(false);
  protected hamburgerMenuVisible = signal<boolean>(false);
  private readonly dialogRef = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly cartService = inject(Cart);
  private readonly guestCartService = inject(GuestCart);
  private readonly myProfileService = inject(MyProfile);
  private readonly utilityService = inject(Utility);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly gtmService = inject(GtmService);
  private readonly dialogCoordinator = inject(AppDialogCoordinator);

  constructor() {
    effect(() => {
      const loggedIn = this.isLoggedIn();
      const addressUpdated = this.appStore.userAddressUpdated();
      if (loggedIn && !this.previousLoginState) {
        this.scheduleDeferredNavbarLoads();
      }
      if (loggedIn && this.previousLoginState && addressUpdated) {
        this.getUserAddress();
      }
      this.previousLoginState = loggedIn;
    });
    effect(() => {
      if (!this.isLoggedIn()) {
        this.appStore.setCartItemCount(this.guestCartService.totalQty());
      }
    });
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId) && this.isLoggedIn()) {
      this.scheduleDeferredNavbarLoads();
    }
  }

  private scheduleDeferredNavbarLoads(): void {
    const load = () => {
      this.getCartCount();
      this.getUserAddress();
      this.getUserWalletBalance();
    };
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(load, { timeout: 3000 });
    } else {
      setTimeout(load, 1500);
    }
  }

  private getCartCount(): void {
    this.cartService
      .getCartCount({
        contactNo: this.appStore.contactNo() || '',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response?.status && response?.data) {
            this.appStore.setCartItemCount(response.data?.adhoc?.totalQuantity);
          }
        },
        error: (err) => {
          this.utilityService.showErrorToaster(err);
        },
      });
  }

  private getUserAddress(): void {
    const contact = this.appStore.contactNo() || '';
    this.myProfileService
      .getDeliveryAddress(contact)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res?.data?.hubDetails) {
            const city = res.data.hubDetails?.hubName || null;
            const pincode = res.data.hubDetails?.pincode || null;
            const hubId = res.data.hubDetails?.hubID || null;
            this.utilityService.setLocalStorageItem<string>(
              UtilityEnum.CURRENT_CITY_STORAGE_KEY,
              city,
            );
            this.utilityService.setLocalStorageItem<string>(
              UtilityEnum.CURRENT_PINCODE_STORAGE_KEY,
              pincode,
            );
            this.utilityService.setLocalStorageItem<number>(
              UtilityEnum.USER_HUB_ID_STORAGE_KEY,
              hubId,
            );
            this.appStore.setUserHubId(hubId);
            this.appStore.setSelectedCity(city);
            this.appStore.setSelectedPincode(pincode);
          }
        },
        error: (err) => {
          this.utilityService.showErrorToaster(err);
        },
      });
  }

  protected openLocationChangeDialog(event: MouseEvent): void {
    this.dialogCoordinator.onUserOpenedOverlay();
    this.dialogCoordinator.closeHomeLocationDialog();
    const isMobile = isPlatformBrowser(this.platformId) && window.innerWidth <= 767;

    if (isMobile) {
      this.dialogRef.open(LocationChangeDialog, {
        disableClose: true,
        autoFocus: false,
        width: '90vw',
        maxWidth: '90vw',
        panelClass: 'location-dialog-mobile',
      });
    } else {
      const target = event.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      this.dialogRef.open(LocationChangeDialog, {
        disableClose: true,
        autoFocus: false,
        width: '441px',
        position: {
          top: `${rect.bottom}px`,
          left: `${rect.left}px`,
        },
      });
    }
  }

  protected openLoginSignupDialog(): void {
    this.dialogCoordinator.onUserOpenedOverlay();
    this.dialogCoordinator.closeHomeLocationDialog();
    const isMobile = isPlatformBrowser(this.platformId) && window.innerWidth <= 767;
    const loginRef = this.dialogRef.open(LoginSignupDialog, {
      disableClose: true,
      autoFocus: false,
      width: isMobile ? '100vw' : '570px',
      height: isMobile ? '100vh' : 'auto',
      maxWidth: isMobile ? '100vw' : undefined,
      maxHeight: isMobile ? '100vh' : undefined,
      panelClass: 'login-dialog-panel',
    });
    loginRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.dialogCoordinator.notifyOverlayClosed();
    });
  }

  protected openCart(): void {
    this.dialogCoordinator.onUserOpenedOverlay();
    this.dialogCoordinator.closeHomeLocationDialog();
    const cartRef = this.dialogRef.open(CartDialog, {
      width: '400px',
      height: '100vh',
      autoFocus: false,
      restoreFocus: false,
      position: { right: '0', top: '0' },
      panelClass: 'cart-dialog-panel',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
    });
    cartRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.dialogCoordinator.notifyOverlayClosed();
    });
  }

  protected navigateToHome(): void {
    this.router.navigate([this.routeEnum.HOME]);
  }

  openUserProfile(): void {
    const isMobile = isPlatformBrowser(this.platformId) && window.innerWidth <= 1023;
    if (isMobile) {
      this.profileSidebarVisible.set(true);
    } else {
      this.router.navigate([this.routeEnum.MY_ACCOUNT]);
    }
  }

  protected openSidebar(): void {
    this.hamburgerMenuVisible.set(true);
  }

  // Hamburger sidebar close handler
  protected onSidebarClose(): void {
    this.hamburgerMenuVisible.set(false);
  }

  protected onNavItemClick(item: SidebarNavItem): void {
    // Optional: Handle navigation item click event if needed
    // This allows parent component to perform additional actions when a nav item is clicked
  }

  protected onSidebarLocationClick(event: MouseEvent): void {
    this.openLocationChangeDialog(event);
  }

  // Profile sidebar close handler
  protected onProfileSidebarClose(): void {
    this.profileSidebarVisible.set(false);
  }

  protected onProfileNavItemClick(item: SidebarNavItem): void {
    // Optional: Handle profile navigation item click
  }

  protected onEditProfileClick(): void {
    this.router.navigate([`${this.routeEnum.MY_ACCOUNT}/${UtilityEnum.EDIT_PROFILE_URL}`]);
  }

  protected onLogoutClick(): void {
    this.gtmService.logOut();
    this.utilityService.deleteAllLocalStorage();
    this.appStore.reset();
    this.router.navigate([RouteEnum.HOME]);
  }

  private getUserWalletBalance(): void {
    const walletDataPayload = {
      contactNo: this.appStore.contactNo() || '',
    };
    this.myProfileService
      .getUserWallet(walletDataPayload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response?.status && response?.data?.length) {
            this.appStore.setUserWalletBalanceProfile(response?.data[0]?.walletBalance || 0);
            this.appStore.setUserWalletBalanceDaysProfile(response?.walletSufficientDays);
            this.utilityService.setCallCenterNo(response?.callCenterNo);
          }
        },
        error: (err) => {
          this.utilityService.showErrorToaster(err);
        },
      });
  }
}
